import { useCallback, useEffect, useMemo, useState } from "react";
import { SURGICAL_ACT_LIMITS } from "../../../classes/constants/surgicalActConstants";
import { useNotification } from "../../../classes/contexts/NotificationContext";
import { usePlanContext } from "../../../classes/contexts/PlanContext";
import { EventsEnum } from "../../../classes/enums/EventsEnum";
import { MedicalImagesType } from "../../../classes/enums/MedicalImagesEnum";
import { SnackbarType } from "../../../classes/enums/SnackbarTypeEnum";
import {
  ActNatures,
  SegementalTransformationTypes,
  TranslationDirections,
} from "../../../classes/enums/SurgicalActsEnum";
import { VertebraSelectionModality } from "../../../classes/enums/VertebraSelectionEnum";
import { vertebraUtils } from "../../../classes/utils/AnnotationsUtils";
import Button from "../../../components/button/Button";
import IncrementDecrementButtons from "../../../components/incrementDecrementButtons/IncrementDecrementButtons";
import MeasurementDisplay from "../../../components/measurementDisplay/MeasurementDisplay";
import LabeledSelect from "../../../components/select/LabeledSelect";
import useSurgicalActActions from "../../../hooks/useSurgicalActActions";
import "./AlignPopupParameterSelector.css";

const SegmentalTransformation = ({
  isEditMode = false,
  existingAct = null,
  onConfirm = null,
  onRequiredFieldsChange = null,
  hasL6 = false,
  hasT13 = false,
}) => {
  const { currentPlanId } = usePlanContext();
  const { addNotification } = useNotification();

  const {
    startVertebraSelection,
    stopVertebraSelection,
    showSegmentalTransformationTool,
    hideSegmentalTransformationTool,
    getSegmentalTransformationAttributes,
    translateSegmentalTransformationTool,
    rotateSegmentalTransformationTool,
  } = useSurgicalActActions(currentPlanId);

  // Tool UI state
  const [
    selectedSegmentalTransformationType,
    setSelectedSegmentalTransformationType,
  ] = useState(SegementalTransformationTypes.SINGLE);
  const [selectedVertebra, setSelectedVertebra] = useState(null);
  const [rotationValue, setRotationValue] = useState(0);
  const [xShiftValue, setXShiftValue] = useState(0);
  const [yShiftValue, setYShiftValue] = useState(0);

  // State to track flow
  const [isVertebraSelectionActive, setIsVertebraSelectionActive] =
    useState(false);
  const [
    isSegmentalTransformationToolActive,
    setIsSegmentalTransformationToolActive,
  ] = useState(false);
  const [
    isLoadingSegmentalTransformationTool,
    setIsLoadingSegmentalTransformationTool,
  ] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  // Bounds from surgical act constants
  const MAX_ROTATION =
    SURGICAL_ACT_LIMITS.SEGMENTAL_TRANSFORMATION.ROTATION.max; // 30
  const MIN_ROTATION =
    SURGICAL_ACT_LIMITS.SEGMENTAL_TRANSFORMATION.ROTATION.min; // -30
  const MAX_TRANSLATION =
    SURGICAL_ACT_LIMITS.SEGMENTAL_TRANSFORMATION.TRANSLATION.max; // 75
  const MIN_TRANSLATION =
    SURGICAL_ACT_LIMITS.SEGMENTAL_TRANSFORMATION.TRANSLATION.min; // -75

  // region CONFIG
  const vertebraItems = useMemo(
    () =>
      ["-", ...vertebraUtils.getAvailableVertebrae(hasL6, hasT13)].map(
        vertebra => ({
          value: vertebra,
          label: vertebra === "-" ? "-" : vertebraUtils.getVertebraLabel(vertebra),
        })
      ),
    [hasL6, hasT13]
  );

  const segmentalTransformationTypes = useMemo(
    () => [
      {
        value: SegementalTransformationTypes.SINGLE,
        label: "Single",
      },
      {
        value: SegementalTransformationTypes.FULL,
        label: "Move Above Spine",
      },
    ],
    []
  );

  const selectedVertebraItem = useMemo(() => {
    if (!selectedVertebra || selectedVertebra === "-") {
      return vertebraItems[0];
    }

    const foundItem = vertebraItems.find(
      item => item.value === selectedVertebra
    );

    return foundItem || vertebraItems[0];
  }, [selectedVertebra, vertebraItems]);

  const selectedSegmentalTransformationTypeItem = useMemo(() => {
    const foundItem = segmentalTransformationTypes.find(
      item => item.value === selectedSegmentalTransformationType
    );

    return foundItem || segmentalTransformationTypes[0];
  }, [selectedSegmentalTransformationType, segmentalTransformationTypes]);

  const getVertebraId = useCallback(vertebra => {
    if (!vertebra || vertebra === "-") return null;
    return vertebraUtils.getCentroidEntity(vertebra);
  }, []);

  const isConfirmDisabled = useMemo(() => {
    const hasValidVertebra = selectedVertebra && selectedVertebra !== "-";
    const hasValidRotation =
      rotationValue >= MIN_ROTATION && rotationValue <= MAX_ROTATION;
    const hasValidXShift =
      xShiftValue >= MIN_TRANSLATION && xShiftValue <= MAX_TRANSLATION;
    const hasValidYShift =
      yShiftValue >= MIN_TRANSLATION && yShiftValue <= MAX_TRANSLATION;

    return (
      !hasValidVertebra ||
      !hasValidRotation ||
      !hasValidXShift ||
      !hasValidYShift ||
      isLoadingSegmentalTransformationTool ||
      isConfirming
    );
  }, [
    selectedVertebra,
    rotationValue,
    xShiftValue,
    yShiftValue,
    MIN_ROTATION,
    MAX_ROTATION,
    MIN_TRANSLATION,
    MAX_TRANSLATION,
    isLoadingSegmentalTransformationTool,
    isConfirming,
  ]);

  // endregion

  // region SETUP
  useEffect(() => {
    startVertebraSelection(
      MedicalImagesType.LATERAL_SURGERY_DX,
      VertebraSelectionModality.SEGMENTAL_TRANSFORMATION,
      1
    );
    setIsVertebraSelectionActive(true);
  }, [startVertebraSelection]);

  // Check if required fields are filled and notify parent
  useEffect(() => {
    if (onRequiredFieldsChange) {
      const hasValidVertebra = selectedVertebra && selectedVertebra !== "-";
      onRequiredFieldsChange("SEGMENTAL_TRANSFORMATION", hasValidVertebra);
    }
  }, [selectedVertebra, onRequiredFieldsChange]);

  useEffect(() => {
    if (isEditMode && existingAct) {
      // Set the vertebra
      if (existingAct.vertebra) {
        setSelectedVertebra(existingAct.vertebra);
      }

      // Set the transformation type
      if (existingAct.type) {
        setSelectedSegmentalTransformationType(existingAct.type);
      }

      // Set transformation parameters
      if (existingAct.rotation !== undefined) {
        setRotationValue(existingAct.rotation);
      }

      if (existingAct.xShift !== undefined) {
        setXShiftValue(existingAct.xShift);
      }

      if (existingAct.yShift !== undefined) {
        setYShiftValue(existingAct.yShift);
      }

      // Stop vertebra selection since we're editing
      if (isVertebraSelectionActive) {
        stopVertebraSelection();
        setIsVertebraSelectionActive(false);
      }
    }
  }, [
    isEditMode,
    existingAct,
    isVertebraSelectionActive,
    stopVertebraSelection,
  ]);

  // region CLEANUP
  useEffect(() => {
    return () => {
      if (isVertebraSelectionActive) {
        stopVertebraSelection();
      }
      if (isSegmentalTransformationToolActive) {
        hideSegmentalTransformationTool();
      }
    };
  }, [
    stopVertebraSelection,
    isVertebraSelectionActive,
    isSegmentalTransformationToolActive,
    hideSegmentalTransformationTool,
  ]);

  // region CALLBACKS
  const handleVertebraSelected = useCallback(event => {
    const tag = event.detail[0].tag;
    if (!tag || tag !== VertebraSelectionModality.SEGMENTAL_TRANSFORMATION) {
      return;
    }

    const vertebraId = event.detail[0].id;
    const vertebraType = vertebraUtils.getVertebraFromId(vertebraId);

    setSelectedVertebra(vertebraType);
  }, []);

  const handleManualVertebraChange = useCallback(
    selectedItem => {
      if (isVertebraSelectionActive) {
        stopVertebraSelection();
        setIsVertebraSelectionActive(false);
      }

      const value = selectedItem.target
        ? selectedItem.target.value
        : selectedItem.value;
      setSelectedVertebra(value === "-" ? null : value);
    },
    [isVertebraSelectionActive, stopVertebraSelection]
  );

  const handleSegmentalTransformationTypeChange = useCallback(selectedItem => {
    const value = selectedItem.target
      ? selectedItem.target.value
      : selectedItem.value;
    setSelectedSegmentalTransformationType(value);
  }, []);

  const showSegmentalErrorNotification = useCallback(
    (
      errorPayload,
      fallbackMessage = "Failed to update segmental transformation."
    ) => {
      if (!errorPayload || errorPayload.isUserNotified) {
        return;
      }

      const message = errorPayload.message || fallbackMessage;

      addNotification(SnackbarType.WARNING, message, {
        title: "Spondylolisthesis Adjustment",
        duration: 0,
      });
    },
    [addNotification]
  );

  // Rotation handlers
  const handleRotationChange = useCallback(
    async targetRotation => {
      const clampedTarget = Math.min(
        Math.max(Number(targetRotation), MIN_ROTATION),
        MAX_ROTATION
      );
      const delta = clampedTarget - rotationValue;
      if (delta === 0) return;

      try {
        const response = await rotateSegmentalTransformationTool(delta);
        if (response && response.segmentalTransformationParameters) {
          setRotationValue(
            response.segmentalTransformationParameters.rotation || clampedTarget
          );
        } else {
          setRotationValue(clampedTarget);
        }
      } catch (error) {
        showSegmentalErrorNotification(error);
      }
    },
    [
      rotationValue,
      rotateSegmentalTransformationTool,
      MIN_ROTATION,
      MAX_ROTATION,
      showSegmentalErrorNotification,
    ]
  );

  // Horizontal translation handlers
  const handleHorizontalTranslationChange = useCallback(
    async targetXShift => {
      const clampedTarget = Math.min(
        Math.max(Number(targetXShift), MIN_TRANSLATION),
        MAX_TRANSLATION
      );
      const delta = clampedTarget - xShiftValue;
      if (delta === 0) return;

      try {
        const response = await translateSegmentalTransformationTool(
          TranslationDirections.HORIZONTAL,
          delta
        );
        if (response && response.segmentalTransformationParameters) {
          setXShiftValue(
            response.segmentalTransformationParameters.xShift || clampedTarget
          );
        } else {
          setXShiftValue(clampedTarget);
        }
      } catch (error) {
        showSegmentalErrorNotification(error);
      }
    },
    [
      xShiftValue,
      translateSegmentalTransformationTool,
      MIN_TRANSLATION,
      MAX_TRANSLATION,
      showSegmentalErrorNotification,
    ]
  );

  // Vertical translation handlers
  const handleVerticalTranslationChange = useCallback(
    async targetYShift => {
      const clampedTarget = Math.min(
        Math.max(Number(targetYShift), MIN_TRANSLATION),
        MAX_TRANSLATION
      );
      const delta = clampedTarget - yShiftValue;
      if (delta === 0) return;

      try {
        const response = await translateSegmentalTransformationTool(
          TranslationDirections.VERTICAL,
          delta
        );
        if (response && response.segmentalTransformationParameters) {
          setYShiftValue(
            response.segmentalTransformationParameters.yShift || clampedTarget
          );
        } else {
          setYShiftValue(clampedTarget);
        }
      } catch (error) {
        showSegmentalErrorNotification(error);
      }
    },
    [
      yShiftValue,
      translateSegmentalTransformationTool,
      MIN_TRANSLATION,
      MAX_TRANSLATION,
      showSegmentalErrorNotification,
    ]
  );

  // Increment and decrement functions
  const incrementRotation = useCallback(
    () => handleRotationChange(rotationValue + 1),
    [rotationValue, handleRotationChange]
  );
  const decrementRotation = useCallback(
    () => handleRotationChange(rotationValue - 1),
    [rotationValue, handleRotationChange]
  );

  const incrementHorizontal = useCallback(
    () => handleHorizontalTranslationChange(xShiftValue + 1),
    [xShiftValue, handleHorizontalTranslationChange]
  );
  const decrementHorizontal = useCallback(
    () => handleHorizontalTranslationChange(xShiftValue - 1),
    [xShiftValue, handleHorizontalTranslationChange]
  );

  const incrementVertical = useCallback(
    () => handleVerticalTranslationChange(yShiftValue + 1),
    [yShiftValue, handleVerticalTranslationChange]
  );
  const decrementVertical = useCallback(
    () => handleVerticalTranslationChange(yShiftValue - 1),
    [yShiftValue, handleVerticalTranslationChange]
  );

  const handleManualSegmentalTransformationToolUpdate = useCallback(
    event => {
      const updateData = event.detail[0];
      if (updateData) {
        if (updateData.error) {
          showSegmentalErrorNotification(updateData.error);
          return;
        }

        if (updateData.rotation !== undefined) {
          setRotationValue(Math.round(updateData.rotation));
        }
        if (updateData.xShift !== undefined) {
          setXShiftValue(Math.round(updateData.xShift));
        }
        if (updateData.yShift !== undefined) {
          setYShiftValue(Math.round(updateData.yShift));
        }
      }
    },
    [showSegmentalErrorNotification]
  );

  const handleConfirm = useCallback(async () => {
    // Prevent multiple confirmations
    if (isConfirming) return;

    // Validate all required fields
    if (!selectedVertebra || selectedVertebra === "-") {
      return;
    }

    setIsConfirming(true);

    try {
      // Get the vertebra ID for support values
      const vertebraId = getVertebraId(selectedVertebra);

      // Get support values at confirmation time
      let supportValues = null;
      if (vertebraId) {
        try {
          supportValues =
            await getSegmentalTransformationAttributes(vertebraId);
        } catch (error) {
          // Continue with confirmation even if support values fail
        }
      }

      // Create the segmental transformation act
      const segmentalTransformationAct = {
        actNature: ActNatures.SegmentalTransformation,
        vertebra: selectedVertebra,
        value: null, // Segmental transformation doesn't have a single value
        type: selectedSegmentalTransformationType,
        rotation: rotationValue,
        xShift: xShiftValue,
        yShift: yShiftValue,
        supportPoints: supportValues,
      };

      // Call the callback to add the surgical act
      if (onConfirm) {
        onConfirm(segmentalTransformationAct);
      } else {
      }
    } catch (error) {
    } finally {
      setIsConfirming(false);
    }
  }, [
    selectedVertebra,
    selectedSegmentalTransformationType,
    rotationValue,
    xShiftValue,
    yShiftValue,
    getVertebraId,
    getSegmentalTransformationAttributes,
    isConfirming,
    onConfirm,
  ]);

  // region EVENT LISTENERS
  useEffect(() => {
    window.addEventListener(
      EventsEnum.VERTEBRA_SELECTION,
      handleVertebraSelected
    );
    return () => {
      window.removeEventListener(
        EventsEnum.VERTEBRA_SELECTION,
        handleVertebraSelected
      );
    };
  }, [handleVertebraSelected]);

  useEffect(() => {
    window.addEventListener(
      EventsEnum.MANUAL_SEGMENTAL_TRANSFORMATION_TOOL_UPDATE,
      handleManualSegmentalTransformationToolUpdate
    );
    return () => {
      window.removeEventListener(
        EventsEnum.MANUAL_SEGMENTAL_TRANSFORMATION_TOOL_UPDATE,
        handleManualSegmentalTransformationToolUpdate
      );
    };
  }, [handleManualSegmentalTransformationToolUpdate]);

  useEffect(() => {
    let timeoutId = null;

    if (selectedVertebra && selectedVertebra !== "-") {
      const vertebraId = getVertebraId(selectedVertebra);

      if (vertebraId) {
        // Add delay before showing the tool
        timeoutId = setTimeout(async () => {
          setIsLoadingSegmentalTransformationTool(true);

          try {
            const segmentalTransformationParameters =
              await showSegmentalTransformationTool(
                vertebraId,
                selectedVertebra,
                selectedSegmentalTransformationType
              );

            // Set the values from the response
            if (segmentalTransformationParameters) {
              setRotationValue(
                Math.round(segmentalTransformationParameters.rotation || 0)
              );
              setXShiftValue(
                Math.round(segmentalTransformationParameters.xShift || 0)
              );
              setYShiftValue(
                Math.round(segmentalTransformationParameters.yShift || 0)
              );
            }

            setIsSegmentalTransformationToolActive(true);
          } catch (error) {
            showSegmentalErrorNotification(
              error,
              "Failed to load segmental transformation tool."
            );
          } finally {
            setIsLoadingSegmentalTransformationTool(false);
          }
        }, 100);
      }
    } else {
      if (isSegmentalTransformationToolActive) {
        hideSegmentalTransformationTool();
        setIsSegmentalTransformationToolActive(false);
        setIsLoadingSegmentalTransformationTool(false);
        setRotationValue(0);
        setXShiftValue(0);
        setYShiftValue(0);
        setSelectedVertebra(null);
      }
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    selectedVertebra,
    selectedSegmentalTransformationType,
    getVertebraId,
    showSegmentalTransformationTool,
    isSegmentalTransformationToolActive,
    hideSegmentalTransformationTool,
    showSegmentalErrorNotification,
  ]);

  // region RENDERING
  return (
    <div className="AlignPopupParameterSelector-container">
      <div className="AlignPopupParameterSelector-scrollable-content">
        <div className="AlignPopupParameterSelector-controls-wrapper">
          <div className="AlignPopupParameterSelector-header">
            {(!selectedVertebra || selectedVertebra === "-") &&
              isVertebraSelectionActive && (
                <div className="App-text-medium AlignPopupParameterSelector-subtitle">
                  Select a point on the image
                </div>
              )}
          </div>

          <LabeledSelect
            label="Vertebra"
            items={vertebraItems}
            selectedItem={selectedVertebraItem}
            onChange={handleManualVertebraChange}
          />

          <LabeledSelect
            label="Type"
            items={segmentalTransformationTypes}
            selectedItem={selectedSegmentalTransformationTypeItem}
            onChange={handleSegmentalTransformationTypeChange}
          />

          <div className="AlignPopupParameterSelector-measurement-container">
            <div
              className={`AlignPopupParameterSelector-label App-text-large ${
                isLoadingSegmentalTransformationTool ||
                isConfirming ||
                !isSegmentalTransformationToolActive
                  ? "disabled"
                  : ""
              }`}
            >
              Rotation
            </div>
            <MeasurementDisplay
              value={rotationValue}
              unit="°"
              disabled={
                isLoadingSegmentalTransformationTool ||
                isConfirming ||
                !isSegmentalTransformationToolActive
              }
            />
            <IncrementDecrementButtons
              onIncrement={incrementRotation}
              onDecrement={decrementRotation}
              onIncrementEnd={() => {}}
              onDecrementEnd={() => {}}
              disabled={
                isLoadingSegmentalTransformationTool ||
                isConfirming ||
                !isSegmentalTransformationToolActive
              }
            />
          </div>

          <div className="AlignPopupParameterSelector-measurement-container">
            <div
              className={`AlignPopupParameterSelector-label App-text-large ${
                isLoadingSegmentalTransformationTool ||
                isConfirming ||
                !isSegmentalTransformationToolActive
                  ? "disabled"
                  : ""
              }`}
            >
              X Shift (Local)
            </div>
            <MeasurementDisplay
              value={xShiftValue}
              unit="mm"
              disabled={
                isLoadingSegmentalTransformationTool ||
                isConfirming ||
                !isSegmentalTransformationToolActive
              }
            />
            <IncrementDecrementButtons
              onIncrement={incrementHorizontal}
              onDecrement={decrementHorizontal}
              onIncrementEnd={() => {}}
              onDecrementEnd={() => {}}
              disabled={
                isLoadingSegmentalTransformationTool ||
                isConfirming ||
                !isSegmentalTransformationToolActive
              }
            />
          </div>

          <div className="AlignPopupParameterSelector-measurement-container">
            <div
              className={`AlignPopupParameterSelector-label App-text-large ${
                isLoadingSegmentalTransformationTool ||
                isConfirming ||
                !isSegmentalTransformationToolActive
                  ? "disabled"
                  : ""
              }`}
            >
              Y Shift (Local)
            </div>
            <MeasurementDisplay
              value={yShiftValue}
              unit="mm"
              disabled={
                isLoadingSegmentalTransformationTool ||
                isConfirming ||
                !isSegmentalTransformationToolActive
              }
            />
            <IncrementDecrementButtons
              onIncrement={incrementVertical}
              onDecrement={decrementVertical}
              onIncrementEnd={() => {}}
              onDecrementEnd={() => {}}
              disabled={
                isLoadingSegmentalTransformationTool ||
                isConfirming ||
                !isSegmentalTransformationToolActive
              }
            />
          </div>
        </div>
      </div>

      <div className="AlignPopupParameterSelector-button-wrapper">
        <Button
          value={isConfirming ? "Confirming..." : "Confirm"}
          onClick={handleConfirm}
          disabled={isConfirmDisabled}
        />
      </div>
    </div>
  );
};

export default SegmentalTransformation;
